<template>
  <div class="session-column">
    <div class="overview-sec">
      セッション
    </div>

    <div class="timer-area">
      <ring-timer
        :start-at="startAt"
        :expire-at="expireAt"
      />

      <div class="session-info">
        <table-start-at
          :table-id="tableId"
          :today="today"
          :start-at="startAt"
          :expire-at="expireAt"
          class="session-start-at"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
        />

        <div class="info-row">
          <span class="info-label">終了予定</span>
          <span class="info-value">{{ expireAt ? hhmm(expireAt) : '--:--' }}</span>
        </div>

        <auto-extension-panel
          :table-id="tableId"
          :expire-at="expireAt"
          :auto-extension-enabled="autoExtensionEnabled"
          :auto-extension-course-name="autoExtensionCourseName"
          @updated:table="$emit('updated:table', $event)"
        />

        <div class="info-row">
          <span class="info-label">合計</span>
          <span class="info-value">
            <span
              class="session-badge"
              :class="{ 'is-extending': extending }"
            >
              {{ extending ? '延長中' : 'セット中' }}
            </span>
            <template v-if="stayMinutes !== null">
              {{ stayMinutes }}分
            </template>
          </span>
        </div>
      </div>
    </div>

    <!-- 合計金額（最終請求額）を主役として表示 -->
    <div class="total-hero">
      <div class="total-hero-label">
        合計（お会計）
      </div>
      <div class="total-hero-value">
        <span class="total-hero-yen">¥</span>{{ Number(total).toLocaleString() }}
      </div>
      <div class="total-hero-sub">
        小計 ¥{{ Number(subtotal).toLocaleString() }}（税サ・割引反映前）
      </div>
    </div>

    <div class="overview-sec">
      セット・延長一覧
    </div>

    <session-timeline
      :table-id="tableId"
      @updated:vending-histories="$emit('updated:vending-histories', $event)"
      @updated:table="$emit('updated:table', $event)"
    />

    <div v-if="currentNominations && currentNominations.length > 0">
      <nomination-histories
        :table-id="tableId"
        :current-nominations="currentNominations"
        @updated:vending-histories="$emit('updated:vending-histories', $event)"
        @updated:table="$emit('updated:table', $event)"
      />
    </div>
  </div>
</template>

<script>
import { inject, ref, computed } from '@vue/composition-api'
import useDateFormat from '@/views/composable/useDateFormat'
import {
  groupCourseHistories,
  buildSessionSegments,
  isExtending,
  totalStayMinutes,
} from '@/views/composable/useCourseSession'
import RingTimer from './RingTimer.vue'
import SessionTimeline from './SessionTimeline.vue'
import TableStartAt from './TableStartAt.vue'
import NominationHistories from './NominationHistories.vue'
import AutoExtensionPanel from './AutoExtensionPanel.vue'

export default {
  components: {
    RingTimer,
    SessionTimeline,
    TableStartAt,
    NominationHistories,
    AutoExtensionPanel,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    today: {
      type: String,
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    subtotal: {
      type: [Number, String],
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    currentNominations: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const vendingHistories = inject('vendingHistories', ref([]))
    const { hhmm } = useDateFormat()

    const segments = computed(() => {
      return buildSessionSegments(groupCourseHistories(vendingHistories.value, hhmm))
    })

    const extending = computed(() => isExtending(segments.value))

    const stayMinutes = computed(() => {
      // NOTE: セグメントは新しい順だが手動時間編集で非順序になる可能性があるため、
      // 最遠のreleaseAtを全体から求める（旧courseOverviewと同等の挙動）
      const releaseAts = segments.value.map(s => s.releaseAt).filter(Boolean)
      const lastReleaseAt = releaseAts.length > 0
        ? new Date(Math.max(...releaseAts.map(at => Date.parse(at)))).toISOString()
        : props.expireAt

      return totalStayMinutes(props.startAt, lastReleaseAt)
    })

    return {
      extending,
      stayMinutes,
      hhmm,
    }
  },
}
</script>

<style lang="scss" scoped>
.timer-area {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-start-at {
  background: transparent !important;
  margin-bottom: 4px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
}

.info-label {
  opacity: 0.6;
}

.info-value {
  font-weight: 600;
}

.session-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 999px;
  margin-right: 6px;
  background: var(--overview-emerald-soft, rgba(52, 211, 153, 0.14));
  color: var(--overview-emerald, #34d399);

  &.is-extending {
    background: var(--overview-accent-softer, rgba(139, 92, 246, 0.14));
    color: var(--overview-accent, #8b5cf6);
  }
}

.total-hero {
  text-align: center;
  padding: 14px 12px 16px;
  margin-bottom: 16px;
  border-radius: 16px;
  background: var(--overview-surface-2, rgba(139, 92, 246, 0.06));
  border: 1px solid var(--overview-accent-soft, rgba(139, 92, 246, 0.25));
}

.total-hero-label {
  font-size: 12px;
  opacity: 0.65;
  margin-bottom: 2px;
}

.total-hero-value {
  font-size: 40px;
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1.2;
}

.total-hero-yen {
  font-size: 24px;
  font-weight: 700;
  margin-right: 2px;
  opacity: 0.8;
}

.total-hero-sub {
  font-size: 12px;
  opacity: 0.55;
  margin-top: 2px;
}
</style>
